import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@ingka/button";
import copyIcon from "@ingka/ssr-icon/paths/copy";
import checkmarkIcon from "@ingka/ssr-icon/paths/checkmark";
import { normalizeColourSpelling } from "../utils/colourLabels";
import styles from "./ComboDescription.module.css";

export interface ItemNoEntry {
    item_no: string;
    countries?: string;
}

export interface ReplacementEntry {
    old_no: string;
    new_no: string;
    countries: string;
}

function ItemNoPopup({ items, replacements, anchorRef, onClose }: {
    items: ItemNoEntry[]; replacements?: ReplacementEntry[];
    anchorRef: React.RefObject<HTMLButtonElement | null>; onClose: () => void;
}) {
    const ref = useRef<HTMLSpanElement>(null);
    const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (anchorRef.current) {
            const r = anchorRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 4, left: r.left });
        }
    }, [anchorRef]);

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node) &&
                anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose, anchorRef]);

    let copyIdx = 0;
    const copy = (e: React.MouseEvent, text: string, idx: number) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIdx(idx);
            setTimeout(onClose, 600);
        });
    };

    const showCountries = items.length > 1 || (replacements && replacements.length > 0);

    if (!pos) return null;
    return (
        <span ref={ref} className={styles.popup} style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
            {items.map((item) => {
                const idx = copyIdx++;
                return (
                    <span key={`item-${idx}`} className={styles.popupContent}>
                        <span className={styles.popupItemNo}>{item.item_no}</span>
                        <Button
                            type="tertiary"
                            size="xsmall"
                            iconOnly
                            ssrIcon={copiedIdx === idx ? checkmarkIcon : copyIcon}
                            className={styles.copyBtn}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => copy(e, item.item_no, idx)}
                            aria-label="Copy"
                        />
                        {showCountries && item.countries && <span className={styles.popupCountries}>{item.countries}</span>}
                    </span>
                );
            })}
            {replacements?.map((r) => {
                const oldIdx = copyIdx++;
                const newIdx = copyIdx++;
                return (
                    <span key={`repl-${oldIdx}`} className={styles.popupContent}>
                        <span className={styles.popupItemNo}>{r.old_no}</span>
                        <Button
                            type="tertiary"
                            size="xsmall"
                            iconOnly
                            ssrIcon={copiedIdx === oldIdx ? checkmarkIcon : copyIcon}
                            className={styles.copyBtn}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => copy(e, r.old_no, oldIdx)}
                            aria-label="Copy old item number"
                        />
                        <span className={styles.popupStatus} data-status="ending">ending</span>
                        <span className={styles.popupArrow}>→</span>
                        <span className={styles.popupItemNo}>{r.new_no}</span>
                        <Button
                            type="tertiary"
                            size="xsmall"
                            iconOnly
                            ssrIcon={copiedIdx === newIdx ? checkmarkIcon : copyIcon}
                            className={styles.copyBtn}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => copy(e, r.new_no, newIdx)}
                            aria-label="Copy new item number"
                        />
                        <span className={styles.popupStatus} data-status="new">new</span>
                        <span className={styles.popupCountries}>{r.countries}</span>
                    </span>
                );
            })}
        </span>
    );
}

export function ItemNoInfo({ children, items, replacements, className }: {
    children: React.ReactNode; className?: string;
    items?: ItemNoEntry[]; replacements?: ReplacementEntry[];
}) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);

    const hasInfo = (items && items.length > 0) || (replacements && replacements.length > 0);

    return (
        <span className={`${styles.line}${className ? ` ${className}` : ""}`}>
            {children}
            {hasInfo && (
                <span className={styles.infoWrap}>
                    <button
                        ref={btnRef}
                        className={styles.infoBtn}
                        type="button"
                        aria-label="Show item numbers"
                        aria-expanded={open}
                        onTouchStart={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                    >ⓘ</button>
                    {open && <ItemNoPopup items={items || []} replacements={replacements} anchorRef={btnRef} onClose={close} />}
                </span>
            )}
        </span>
    );
}

function FrameLine({ qty, desc, tag, items, replacements }: {
    qty: number | null; desc: string; tag: string | null;
    items?: ItemNoEntry[]; replacements?: ReplacementEntry[];
}) {
    // Split trailing (color) from desc, e.g. "PAX 150x58x236 (white)" → "PAX 150x58x236" + "(white)"
    const colorMatch = desc.match(/^(.+?)(\s*\([^)]+\))$/);
    const mainDesc = colorMatch ? colorMatch[1] : desc;
    const colorPart = colorMatch ? colorMatch[2] : null;

    return (
        <ItemNoInfo items={items} replacements={replacements}>
            {qty && qty > 1 && <><span className={styles.qtyHighlight}>{qty}×</span>{" "}</>}
            {qty === 1 && `${qty}× `}
            {mainDesc}
            {colorPart && (
                <span className={styles.color}>
                    {normalizeColourSpelling(colorPart)}
                </span>
            )}
            {tag && <span className={styles.frameType}>{tag}</span>}
        </ItemNoInfo>
    );
}

interface ComboDescriptionProps {
    text: string;
    /** Maps item description (without qty prefix) to item number entries */
    itemNoLookup?: Record<string, ItemNoEntry[]>;
    /** Maps item description to replacement entries (product renewals) */
    replacementsLookup?: Record<string, ReplacementEntry[]>;
}

/** Renders a multi-line combo description, bolding quantity prefixes > 1×,
 *  dimming frame type suffixes, and showing item number popups on (i) click. */
export function ComboDescription({ text, itemNoLookup, replacementsLookup }: ComboDescriptionProps) {
    const lines = text.split("\n");

    return (
        <span className={styles.combo}>
            {lines.map((line, i) => {
                const match = line.match(/^(\d+)× (.+)$/);
                const qty = match ? parseInt(match[1]) : null;
                const rest = match ? match[2] : line;
                const tagIdx = rest.indexOf(" · ");
                const desc = tagIdx >= 0 ? rest.slice(0, tagIdx) : rest;
                const tag = tagIdx >= 0 ? rest.slice(tagIdx) : null;
                const items = itemNoLookup?.[rest];
                const repls = replacementsLookup?.[rest];
                return <FrameLine key={i} qty={qty} desc={desc} tag={tag} items={items} replacements={repls} />;
            })}
        </span>
    );
}
